import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import {
    collection,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    Timestamp,
    query,
    orderBy
} from "firebase/firestore";
import { db } from "../firebase";
import Loader from "../components/Loader";

const COLLECTION_NAME = "ITEM_MASTER";

const ItemMaster = forwardRef((props, ref) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        type: "Goods", // Goods or Services
        hsnSac: "",
        partNumber: "",
        description: "",
        purchasePrice: "",
        sellingPrice: "",
        defaultRate: "",
        unit: "Nos",
        taxRate: 18
    });
    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;

    useImperativeHandle(ref, () => ({
        openAddModal: () => {
            setEditingId(null);
            setFormData({
                name: "",
                type: "Goods",
                hsnSac: "",
                partNumber: "",
                description: "",
                purchasePrice: "",
                sellingPrice: "",
                defaultRate: "",
                unit: "Nos",
                taxRate: 18
            });
            setShowModal(true);
        }
    }));

    useEffect(() => {
        fetchItems();
    }, []);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, COLLECTION_NAME), orderBy("name"));
            const snap = await getDocs(q);
            const itemsList = snap.docs.map(d => ({ id: d.id, ...d.data() }));

            // Fetch Part Stock to show current quantity
            const stockSnap = await getDocs(collection(db, "PART_STOCK"));
            const stockByPartNo = {};
            const stockByName = {};

            stockSnap.docs.forEach(d => {
                const data = d.data();
                const name = (data.name || "").trim().toLowerCase();
                const partNo = (data.partNumber || "").trim().toLowerCase();
                const qty = data.goodQty || 0;

                if (partNo) stockByPartNo[partNo] = qty;
                if (name) stockByName[name] = qty;
            });

            setItems(itemsList.map(item => {
                const itemPartNo = (item.partNumber || "").trim().toLowerCase();
                const itemName = (item.name || "").trim().toLowerCase();
                let qty = 0;

                if (itemPartNo && stockByPartNo[itemPartNo] !== undefined) {
                    qty = stockByPartNo[itemPartNo];
                } else if (itemName && stockByName[itemName] !== undefined) {
                    qty = stockByName[itemName];
                }

                return { ...item, stockQty: qty };
            }));
        } catch (err) {
            console.error("Error fetching items:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.hsnSac) return alert("Name and HSN/SAC are required");

        setLoading(true);
        try {
            const data = {
                ...formData,
                purchasePrice: Number(formData.purchasePrice || 0),
                sellingPrice: Number(formData.sellingPrice || 0),
                defaultRate: Number(formData.sellingPrice || formData.defaultRate || 0)
            };

            let itemId = editingId;
            if (editingId) {
                await updateDoc(doc(db, COLLECTION_NAME, editingId), data);
            } else {
                const docRef = await addDoc(collection(db, COLLECTION_NAME), {
                    ...data,
                    createdAt: Timestamp.now()
                });
                itemId = docRef.id;
            }

            // Sync with Part Stock
            const stockSnap = await getDocs(collection(db, "PART_STOCK"));
            const existingPart = stockSnap.docs.find(d => {
                const p = d.data();
                return p.name.toLowerCase() === data.name.toLowerCase() && p.partNumber === data.partNumber;
            });

            const partData = {
                name: data.name,
                partNumber: data.partNumber || "",
                model: data.description || "",
                purchasePrice: data.purchasePrice,
                sellingPrice: data.sellingPrice,
                hsnSac: data.hsnSac,
                type: data.type || "Goods",
                updatedAt: Timestamp.now()
            };

            if (existingPart) {
                await updateDoc(doc(db, "PART_STOCK", existingPart.id), partData);
            } else if (data.type === "Goods") {
                await addDoc(collection(db, "PART_STOCK"), {
                    ...partData,
                    goodQty: 0,
                    defectiveQty: 0,
                    location: "GRPD", // Default location
                    remarks: "Created from Item Master",
                    createdAt: Timestamp.now()
                });
            }

            alert(editingId ? "Item updated successfully" : "Item added successfully");
            setShowModal(false);
            setEditingId(null);
            fetchItems();
        } catch (err) {
            console.error("Error saving item:", err);
            alert("Failed to save item");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (item) => {
        setFormData({
            name: item.name,
            type: item.type || "Goods",
            hsnSac: item.hsnSac || "",
            partNumber: item.partNumber || "",
            description: item.description || "",
            purchasePrice: item.purchasePrice || "",
            sellingPrice: item.sellingPrice || "",
            defaultRate: item.defaultRate || "",
            unit: item.unit || "Nos",
            taxRate: item.taxRate || 18
        });
        setEditingId(item.id);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this item?")) return;
        setLoading(true);
        try {
            await deleteDoc(doc(db, COLLECTION_NAME, id));
            fetchItems();
        } catch (err) {
            console.error("Error deleting item:", err);
        } finally {
            setLoading(false);
        }
    };

    const filteredItems = items.filter(i =>
        i.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (i.hsnSac && i.hsnSac.includes(searchTerm))
    );

    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const paginatedItems = filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="">
            <div className="card shadow-sm border-0 mb-3">
                <div className="card-body py-2">
                    <div className="row g-2 align-items-center">
                        <div className="col-md-6">
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white">🔍</span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by name or HSN/SAC..."
                                    value={searchTerm}
                                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                />
                            </div>
                        </div>
                        <div className="col-md text-end text-muted small">
                            Total {filteredItems.length} record(s)
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <Loader />
            ) : (
                <div className="card shadow-sm border-0">
                    <div className="card-header bg-white py-3">
                        <h5 className="mb-0 text-primary">Item List</h5>
                    </div>
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Name / Description</th>
                                    <th>HSN/SAC</th>
                                    <th>Type</th>
                                    <th>Purchase</th>
                                    <th>Selling</th>
                                    <th>Margin</th>
                                    <th>Qty</th>
                                    <th>Unit</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedItems.map(item => (
                                    <tr key={item.id} className="table-row-hover">
                                        <td>
                                            <div className="fw-bold">{item.name}</div>
                                            {item.partNumber && <code className="small text-primary">{item.partNumber}</code>}
                                            <div className="small text-muted text-truncate" style={{ maxWidth: "200px" }}>{item.description}</div>
                                        </td>
                                        <td>{item.hsnSac}</td>
                                        <td>
                                            <span className={`badge ${item.type === 'Services' ? 'bg-info-subtle text-info' : 'bg-success-subtle text-success'}`}>
                                                {item.type || 'Goods'}
                                            </span>
                                        </td>
                                        <td>₹{item.purchasePrice || 0}</td>
                                        <td>₹{item.sellingPrice || 0}</td>
                                        <td className="fw-bold text-success">
                                            ₹{(Number(item.sellingPrice || 0) - Number(item.purchasePrice || 0)).toFixed(2)}
                                        </td>
                                        <td>
                                            {item.type !== 'Services' ? (
                                                <span className={`badge ${item.stockQty > 0 ? 'bg-success' : 'bg-danger'}`}>
                                                    {item.stockQty || 0}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td>{item.unit}</td>
                                        <td className="text-end">
                                            <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEdit(item)}>✏</button>
                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(item.id)}>🗑</button>
                                        </td>
                                    </tr>
                                ))}
                                {filteredItems.length === 0 && (
                                    <tr><td colSpan="6" className="text-center py-4">No items found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3 border-top pt-3">
                    <div className="small text-muted">
                        Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredItems.length)} of {filteredItems.length} records
                    </div>
                    <div className="btn-group btn-group-sm shadow-sm">
                        <button
                            className="btn btn-white border px-3"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(p => p - 1)}
                        >
                            &laquo; Prev
                        </button>
                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i}
                                className={`btn ${currentPage === i + 1 ? 'btn-primary' : 'btn-white border'}`}
                                onClick={() => setCurrentPage(i + 1)}
                            >
                                {i + 1}
                            </button>
                        ))}
                        <button
                            className="btn btn-white border px-3"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(p => p + 1)}
                        >
                            Next &raquo;
                        </button>
                    </div>
                </div>
            )}

            {showModal && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content shadow-lg border-0">
                            <form onSubmit={handleSubmit}>
                                <div className="modal-header bg-light">
                                    <h5 className="modal-title">{editingId ? "Edit Item" : "Add Item"}</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                                </div>
                                <div className="modal-body p-4">
                                    <div className="row g-3">
                                        <div className="col-md-5">
                                            <label className="fw-bold small mb-1">Item Name</label>
                                            <input
                                                className="form-control"
                                                required
                                                placeholder="Enter item name"
                                                value={formData.name}
                                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="fw-bold small mb-1">Type</label>
                                            <select
                                                className="form-select border-primary fw-bold"
                                                value={formData.type}
                                                onChange={e => setFormData({ ...formData, type: e.target.value })}
                                            >
                                                <option value="Goods">📦 Goods</option>
                                                <option value="Services">🛠 Services</option>
                                            </select>
                                        </div>
                                        <div className="col-md-4">
                                            <label className="fw-bold small mb-1">HSN/SAC Code</label>
                                            <input
                                                className="form-control"
                                                required
                                                placeholder="HSN code"
                                                value={formData.hsnSac}
                                                onChange={e => setFormData({ ...formData, hsnSac: e.target.value })}
                                            />
                                        </div>

                                        {formData.type === "Goods" && (
                                            <>
                                                <div className="col-md-4">
                                                    <label className="fw-bold small mb-1">Part Number</label>
                                                    <input
                                                        className="form-control bg-light"
                                                        placeholder="Part Number"
                                                        value={formData.partNumber}
                                                        onChange={e => setFormData({ ...formData, partNumber: e.target.value })}
                                                    />
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="fw-bold small mb-1">Purchase Price</label>
                                                    <div className="input-group">
                                                        <span className="input-group-text">₹</span>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            placeholder="0.00"
                                                            value={formData.purchasePrice}
                                                            onChange={e => setFormData({ ...formData, purchasePrice: e.target.value })}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="fw-bold small mb-1">Selling Price</label>
                                                    <div className="input-group">
                                                        <span className="input-group-text">₹</span>
                                                        <input
                                                            type="number"
                                                            className="form-control border-primary"
                                                            placeholder="0.00"
                                                            value={formData.sellingPrice}
                                                            onChange={e => setFormData({ ...formData, sellingPrice: e.target.value })}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-12 mt-0">
                                                    <div className="alert alert-info py-1 px-3 small d-flex justify-content-between align-items-center mb-0">
                                                        <span>Expect Margin: <strong className="text-success">₹{(Number(formData.sellingPrice || 0) - Number(formData.purchasePrice || 0)).toFixed(2)}</strong></span>
                                                        <span>Margin %: <strong className="text-primary">{formData.purchasePrice > 0 ? (((formData.sellingPrice - formData.purchasePrice) / formData.purchasePrice) * 100).toFixed(1) : 0}%</strong></span>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {formData.type === "Services" && (
                                            <div className="col-md-4">
                                                <label className="fw-bold small mb-1">Service Rate</label>
                                                <div className="input-group">
                                                    <span className="input-group-text">₹</span>
                                                    <input
                                                        type="number"
                                                        className="form-control border-primary"
                                                        placeholder="0.00"
                                                        value={formData.sellingPrice || formData.defaultRate}
                                                        onChange={e => setFormData({ ...formData, sellingPrice: e.target.value, defaultRate: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <div className="col-md-4">
                                            <label className="fw-bold small mb-1">GST %</label>
                                            <select
                                                className="form-select"
                                                value={formData.taxRate}
                                                onChange={e => setFormData({ ...formData, taxRate: Number(e.target.value) })}
                                            >
                                                <option value="0">0% (Exempt)</option>
                                                <option value="5">5%</option>
                                                <option value="12">12%</option>
                                                <option value="18">18%</option>
                                                <option value="28">28%</option>
                                            </select>
                                        </div>
                                        <div className="col-md-4">
                                            <label className="fw-bold small mb-1">Unit</label>
                                            <select
                                                className="form-select"
                                                value={formData.unit}
                                                onChange={e => setFormData({ ...formData, unit: e.target.value })}
                                            >
                                                <option value="Nos">Nos</option>
                                                <option value="Sets">Sets</option>
                                                <option value="Hrs">Hrs</option>
                                                <option value="Ltr">Ltr</option>
                                                <option value="Kg">Kg</option>
                                                <option value="Mtr">Mtr</option>
                                            </select>
                                        </div>
                                        <div className="col-12">
                                            <label className="fw-bold small mb-1">Description (Optional)</label>
                                            <textarea
                                                className="form-control"
                                                rows="3"
                                                placeholder="Enter item description"
                                                value={formData.description}
                                                onChange={e => setFormData({ ...formData, description: e.target.value })}
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer bg-light">
                                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary px-4" disabled={loading}>
                                        {loading ? <span className="spinner-border spinner-border-sm" /> : (editingId ? "Update Item" : "Save Item")}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

export default ItemMaster;
